import { z } from 'zod';
// ============================================================================
// CORE TYPES
// ============================================================================
export const PlayerSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(20),
    color: z.string(),
    avatar: z.string().optional(),
    isHost: z.boolean().default(false),
    connected: z.boolean().default(true),
    score: z.number().default(0),
    streak: z.number().default(0),
});
export const PlayerStateSchema = PlayerSchema.extend({
    hasAnswered: z.boolean().default(false),
    answer: z.string().nullable().default(null),
    rank: z.number().nullable().default(null),
    answerTime: z.number().nullable().default(null),
});
// ============================================================================
// QUESTION TYPES
// ============================================================================
export const QuestionTypeSchema = z.enum([
    'text',
    'numeric',
    'multiple-choice',
    'order',
]);
// Difficulty is the "depth" axis: it drives XP rewards (harder = more XP) and
// can gate which questions a game draws. Optional so legacy questions keep
// working; when absent, scoring treats it as 'medium'.
export const DifficultySchema = z.enum(['easy', 'medium', 'hard']);
export const QuestionSchema = z.object({
    id: z.string(),
    type: QuestionTypeSchema,
    prompt: z.string(),
    category: z.string(),
    theme: z.string().optional(),
    difficulty: DifficultySchema.optional(),
    // The bank only grows. A question is part of a game's pool when addedOn <= the
    // game's date (absent = the original bank) and the game predates retiredOn.
    // Retire a question with retiredOn; never delete one (see shared/src/bank.ts).
    addedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    retiredOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    // Set when a person has looked at the player reports on this question and fixed it
    // (or decided it is fine): reports up to and including that day no longer count
    // towards retiring it (see shared/src/quarantine.ts).
    reviewedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    timeLimit: z.number().default(30),
    basePoints: z.number().default(100),
    correctAnswer: z.union([z.string(), z.number(), z.array(z.string())]).optional(),
    options: z.array(z.string()).optional(),
    items: z.array(z.string()).optional(),
    validation: z.object({
        caseSensitive: z.boolean().default(false),
        fuzzyMatch: z.boolean().default(false),
        tolerance: z.number().optional(),
    }).optional(),
});
export const RoundSchema = z.object({
    id: z.string(),
    question: QuestionSchema,
    state: z.enum(['waiting', 'answering', 'revealing', 'ranking', 'results', 'complete']),
    startedAt: z.number().nullable().default(null),
    endedAt: z.number().nullable().default(null),
    answers: z.record(z.string(), z.string()).default({}),
    rankings: z.record(z.string(), z.number()).default({}),
    correctness: z.record(z.string(), z.boolean()).default({}),
});
// ============================================================================
// GAME STATE
// ============================================================================
export const GamePhaseSchema = z.enum([
    'lobby',
    'countdown',
    'playing',
    'revealing',
    'ranking',
    'intermission',
    'final',
    'ended',
]);
export const GameSettingsSchema = z.object({
    maxPlayers: z.number().int().min(2).max(16).default(8),
    rounds: z.number().int().min(1).max(100).default(20),
    roundTimeLimit: z.number().int().min(5).max(120).default(30),
    // Empty = no filter (draw trivia from the whole bank). Non-empty restricts
    // the trivia pool to just these category names.
    categories: z.array(z.string().max(64)).max(64).default([]),
    // Optional theme filters within selected categories. Format: "Category|Theme"
    // e.g., "Geography|Capitals & Cities". Empty = all themes in selected categories.
    themes: z.array(z.string().max(128)).max(256).default([]),
});
// Deliberately less than the full question bank (72 MCQ) so each game draws
// a random subset — replays feel different instead of always cycling
// through every question in the same (just reshuffled) set.
export const DEFAULT_SETTINGS = {
    maxPlayers: 8,
    rounds: 12,
    roundTimeLimit: 30,
    categories: [],
    themes: [],
};
export const GameStateSchema = z.object({
    id: z.string(),
    code: z.string(),
    phase: GamePhaseSchema,
    settings: GameSettingsSchema,
    players: z.array(PlayerStateSchema).default([]),
    currentRound: z.number().default(0),
    rounds: z.array(RoundSchema).default([]),
    questionPool: z.array(QuestionSchema).default([]),
    // Category metadata for lobby — key, title, icon, color, count
    availableCategories: z.array(z.object({
        key: z.string(),
        title: z.string(),
        icon: z.string(),
        color: z.string(),
        count: z.number(),
    })).default([]),
    createdAt: z.number(),
    startedAt: z.number().nullable().default(null),
    endedAt: z.number().nullable().default(null),
    hostId: z.string(),
    // Players who were in the room when the current game started. Anyone who
    // joins the same link later waits in the room and plays the next game.
    participantIds: z.array(z.string()).default([]),
});
// ============================================================================
// MESSAGES (Client <-> Server)
// ============================================================================
export const ClientMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('join'), payload: z.object({ name: z.string(), code: z.string(), avatar: z.string().max(16).optional() }) }),
    z.object({ type: z.literal('create'), payload: z.object({ name: z.string(), settings: GameSettingsSchema.partial(), avatar: z.string().max(16).optional() }) }),
    z.object({ type: z.literal('start'), payload: z.object({}) }),
    z.object({ type: z.literal('answer'), payload: z.object({ roundId: z.string(), answer: z.string().max(500) }) }),
    z.object({ type: z.literal('force-next'), payload: z.object({ roundId: z.string() }) }),
    z.object({ type: z.literal('play-again'), payload: z.object({}) }),
    z.object({ type: z.literal('leave'), payload: z.object({}) }),
    z.object({ type: z.literal('ping'), payload: z.object({}) }),
    z.object({ type: z.literal('update-settings'), payload: GameSettingsSchema.partial() }),
    z.object({ type: z.literal('kick-player'), payload: z.object({ playerId: z.string() }) }),
    z.object({ type: z.literal('react'), payload: z.object({ emoji: z.string() }) }),
]);
// The only emojis players can fling during a round. A fixed allow-list keeps
// the reaction stream fun and prevents arbitrary/abusive payloads.
export const REACTION_EMOJIS = ['😂', '🔥', '💀', '😮', '👏', '🧠'];
export const ServerMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('state'), payload: z.object({ game: GameStateSchema, you: PlayerStateSchema }) }),
    z.object({ type: z.literal('joined'), payload: z.object({ game: GameStateSchema, you: PlayerStateSchema }) }),
    z.object({ type: z.literal('player-joined'), payload: z.object({ player: PlayerSchema }) }),
    z.object({ type: z.literal('player-left'), payload: z.object({ playerId: z.string() }) }),
    z.object({ type: z.literal('phase-changed'), payload: z.object({ phase: GamePhaseSchema }) }),
    z.object({ type: z.literal('round-started'), payload: z.object({ round: RoundSchema }) }),
    z.object({ type: z.literal('round-ended'), payload: z.object({ round: RoundSchema, scores: z.record(z.string(), z.number()) }) }),
    z.object({ type: z.literal('answer-received'), payload: z.object({ playerId: z.string(), hasAnswered: z.boolean() }) }),
    z.object({ type: z.literal('reveal-answer'), payload: z.object({ playerId: z.string(), answer: z.string(), rank: z.number().nullable(), correct: z.boolean(), correctAnswer: z.union([z.string(), z.number(), z.array(z.string())]).optional() }) }),
    z.object({ type: z.literal('scores-updated'), payload: z.object({ scores: z.record(z.string(), z.number()) }) }),
    z.object({ type: z.literal('error'), payload: z.object({ code: z.string(), message: z.string() }) }),
    z.object({ type: z.literal('pong'), payload: z.object({}) }),
    z.object({ type: z.literal('kicked'), payload: z.object({ reason: z.string() }) }),
    z.object({ type: z.literal('settings-updated'), payload: GameSettingsSchema }),
    z.object({ type: z.literal('leaderboard'), payload: z.object({ final: z.array(z.object({ playerId: z.string(), name: z.string(), score: z.number() })) }) }),
    z.object({ type: z.literal('reaction'), payload: z.object({ playerId: z.string(), emoji: z.string() }) }),
]);
